// The admin panel (D-0020) talks to two things, both same-origin so the session cookie rides along:
// Better Auth's admin plugin for people, roles, bans and sessions (/api/auth/admin/*), and control-api's
// /api/admin/* for what that plugin does not know — workspaces, and the platform's public assets.

export interface AdminMe {
  id: string;
  email: string;
  name: string;
  image: string | null;
  admin: boolean;
}
export interface PlatformUser {
  id: string;
  name: string;
  email: string;
  image?: string | null;
  role?: string | null;
  banned?: boolean | null;
  banReason?: string | null;
  createdAt: string;
}
export interface UserSession {
  id: string;
  token: string;
  createdAt: string;
  expiresAt: string;
  ipAddress?: string | null;
  userAgent?: string | null;
}
export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  createdAt: string;
  members: number;
}
export interface WorkspaceMember {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  name: string;
  email: string;
  image: string | null;
}
export interface WorkspaceDetail extends Omit<WorkspaceSummary, "members"> {
  members: WorkspaceMember[];
}
/** A file anyone can read by its URL: a connector's logo, odds and ends. */
export interface Asset {
  key: string;
  name: string;
  contentType: string;
  size: number;
  updatedAt: string;
  url: string;
}
export interface Page<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

export const PAGE_SIZE = 25;
const SESSION_REFRESH_MS = 30_000;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export type Fetch = typeof fetch;

export function createApi(doFetch: Fetch = (...args) => fetch(...args)) {
  /** `body` is sent as JSON; `file` as it is. */
  async function request<T>(path: string, init: { method?: string; body?: unknown; file?: Blob } = {}): Promise<T> {
    let res: Response;
    try {
      res = await doFetch(`/api${path}`, {
        method: init.method ?? "GET",
        credentials: "include",
        headers: init.file ? { "content-type": "application/octet-stream" } : init.body === undefined ? {} : { "content-type": "application/json" },
        ...(init.file ? { body: init.file } : init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      });
    } catch {
      throw new ApiError(0, "network_error", "无法连接服务器。");
    }
    if (res.status === 204) return undefined as T;
    const body = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
    if (!res.ok) throw new ApiError(res.status, body.code ?? "error", body.message ?? res.statusText);
    return body as T;
  }
  // The shell asks who is signed in before every page. The answer changes only on sign-in and
  // sign-out, so it is kept and refreshed behind the next click once it is a while old: a click
  // switches the page without waiting on it. A failed answer is not kept.
  let session: { me: Promise<AdminMe>; at: number } | null = null;
  const readMe = () => {
    const entry = { me: request<AdminMe>("/admin/me"), at: Date.now() };
    entry.me.catch(() => {
      if (session === entry) session = null;
    });
    session = entry;
    return entry.me;
  };
  const forgetMe = <T>(p: Promise<T>) =>
    p.finally(() => {
      session = null;
    });
  const query = (params: Record<string, string | number | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") q.set(k, String(v));
    return q.size ? `?${q}` : "";
  };

  return {
    me: () => {
      if (!session) return readMe();
      const kept = session.me;
      if (Date.now() - session.at > SESSION_REFRESH_MS) void readMe().catch(() => undefined);
      return kept;
    },
    signIn: (email: string, password: string) => forgetMe(request<unknown>("/auth/sign-in/email", { method: "POST", body: { email, password } })),
    signInWithGitHub: (callbackURL: string) => request<{ url: string }>("/auth/sign-in/social", { method: "POST", body: { provider: "github", callbackURL } }),
    signOut: () => forgetMe(request<unknown>("/auth/sign-out", { method: "POST", body: {} })),

    // People — Better Auth's admin plugin. Acting as someone, setting a password, and creating,
    // editing or deleting accounts are switched off on the server.
    users: ({ search = "", offset = 0 }: { search?: string; offset?: number }) =>
      request<{ users: PlatformUser[]; total: number }>(`/auth/admin/list-users${query({ limit: PAGE_SIZE, offset, sortBy: "createdAt", sortDirection: "desc", ...(search ? { searchField: "email", searchOperator: "contains", searchValue: search } : {}) })}`).then(
        (r): Page<PlatformUser> => ({ items: r.users, total: r.total, offset, limit: PAGE_SIZE }),
      ),
    user: (id: string) => request<PlatformUser>(`/auth/admin/get-user${query({ id })}`),
    setRole: (userId: string, role: "admin" | "user") => request<unknown>("/auth/admin/set-role", { method: "POST", body: { userId, role } }),
    banUser: (userId: string, banReason: string) => request<unknown>("/auth/admin/ban-user", { method: "POST", body: { userId, ...(banReason ? { banReason } : {}) } }),
    unbanUser: (userId: string) => request<unknown>("/auth/admin/unban-user", { method: "POST", body: { userId } }),
    sessions: (userId: string) => request<{ sessions: UserSession[] }>("/auth/admin/list-user-sessions", { method: "POST", body: { userId } }).then((r) => r.sessions),
    revokeSession: (sessionToken: string) => request<unknown>("/auth/admin/revoke-user-session", { method: "POST", body: { sessionToken } }),
    revokeSessions: (userId: string) => request<unknown>("/auth/admin/revoke-user-sessions", { method: "POST", body: { userId } }),

    workspaces: ({ search = "", offset = 0 }: { search?: string; offset?: number }) => request<Page<WorkspaceSummary>>(`/admin/workspaces${query({ q: search, offset })}`),
    workspace: (id: string) => request<WorkspaceDetail>(`/admin/workspaces/${encodeURIComponent(id)}`),

    assets: () => request<{ items: Asset[] }>("/admin/assets").then((r) => r.items),
    uploadAsset: (file: File) => request<Asset>(`/admin/assets${query({ name: file.name })}`, { method: "POST", file }),
    deleteAsset: (key: string) => request<void>(`/admin/assets/${key.replace(/^public\//, "")}`, { method: "DELETE" }),
  };
}

export type Api = ReturnType<typeof createApi>;

export const isAdmin = (u: Pick<PlatformUser, "role">) => (u.role ?? "").split(",").some((r) => r.trim() === "admin");
export const formatSize = (bytes: number) => (bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);
export const formatDate = (at: string) => new Date(at).toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" });
